import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Save, Zap, Trash2, Search, Users, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, fecha, norm } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const FilaFamilia = ({ fam, descuentos, onGuardado }) => {
  const [abierto, setAbierto] = useState(false);
  const [cfg, setCfg] = useState({
    activo: fam.activo, concepto: fam.concepto || "", dia_emision: fam.dia_emision,
    dia_vencimiento: fam.dia_vencimiento, fin_de_mes: fam.fin_de_mes,
    descuento_ids: fam.descuento_ids,
    montos: Object.fromEntries(fam.estudiantes.map((e) => [e.id, e.monto || ""])),
  });
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);

  const total = fam.estudiantes.reduce((s, e) => s + (Number(cfg.montos[e.id]) || 0), 0);

  const repartir = (valor) => {
    const monto = Number(valor) || 0;
    const cuantos = fam.estudiantes.length || 1;
    const parte = Math.round((monto / cuantos) * 100) / 100;
    const ajuste = Math.round((monto - parte * cuantos) * 100) / 100;
    setCfg({
      ...cfg,
      montos: Object.fromEntries(fam.estudiantes.map((e, i) => [
        e.id, String(i === 0 ? Math.round((parte + ajuste) * 100) / 100 : parte),
      ])),
    });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/mensualidades/${fam.tutor_id}`, {
        activo: cfg.activo, concepto: cfg.concepto.trim() || null,
        dia_emision: Number(cfg.dia_emision) || 1,
        dia_vencimiento: Number(cfg.dia_vencimiento) || 10,
        fin_de_mes: cfg.fin_de_mes, descuento_ids: cfg.descuento_ids,
        lineas: fam.estudiantes.map((e) => ({ estudiante_id: e.id,
                                              monto: Number(cfg.montos[e.id]) || 0 })),
      });
      toast.success(`Mensualidad de ${fam.tutor_nombre} guardada: ${money(total)}`);
      onGuardado();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const generarAhora = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post("/mensualidades/generar", { tutor_id: fam.tutor_id });
      if (data.generadas) {
        const f = data.facturas[0];
        toast.success(`Factura ${f.ncf} emitida por ${money(f.monto_total)}`);
      } else if (data.ya_estaban) {
        toast.warning("Esta familia ya tiene su factura de este mes");
      } else {
        toast.warning("No hay montos configurados para generar la factura");
      }
      onGuardado();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerando(false);
    }
  };

  const quitar = async () => {
    await api.delete(`/mensualidades/${fam.tutor_id}`);
    toast.success("Mensualidad automática retirada de esta familia");
    onGuardado();
  };

  return (
    <article className="rounded-lg border border-border/60 bg-card"
             data-testid={`familia-${fam.tutor_id}`}>
      <button onClick={() => setAbierto(!abierto)} data-testid={`abrir-familia-${fam.tutor_id}`}
              className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left">
        <span className="min-w-0">
          <span className="block truncate font-display text-base font-semibold">
            {fam.tutor_nombre}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {fam.estudiantes.length} hijo(s) · {fam.estudiantes.map((e) => e.nombre).join(", ")}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-2">
          {fam.generada_este_mes && (
            <Badge variant="secondary" data-testid={`generada-${fam.tutor_id}`}>
              facturada este mes
            </Badge>
          )}
          <Badge variant={cfg.activo ? "default" : "outline"}>
            {cfg.activo ? "automática" : "sin configurar"}
          </Badge>
          <span className="font-mono font-semibold" data-testid={`total-familia-${fam.tutor_id}`}>
            {money(total)}
          </span>
        </span>
      </button>

      {abierto && (
        <div className="space-y-5 border-t border-border/60 p-5">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={cfg.activo} data-testid={`activo-${fam.tutor_id}`}
                      onCheckedChange={(v) => setCfg({ ...cfg, activo: !!v })} />
            <span>
              <span className="font-semibold">Facturar sola cada mes</span>
              <span className="block text-xs text-muted-foreground">
                El día indicado se emite una factura consolidada a nombre del tutor, sin valor
                fiscal, lista para que usted la revise, le agregue conceptos o la haga fiscal.
              </span>
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Concepto</Label>
              <Input value={cfg.concepto} placeholder="Mensualidad (automático por mes)"
                     data-testid={`concepto-${fam.tutor_id}`}
                     onChange={(e) => setCfg({ ...cfg, concepto: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Monto total de la familia (se reparte entre los hijos)</Label>
              <Input type="number" placeholder="Ej. 7000" data-testid={`repartir-${fam.tutor_id}`}
                     onChange={(e) => repartir(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Monto por estudiante
            </Label>
            {fam.estudiantes.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-3 rounded-md bg-muted/30 p-3">
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block truncate font-medium">{e.nombre}</span>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {e.matricula} · {e.grado}{e.seccion ? ` ${e.seccion}` : ""}
                  </span>
                </span>
                <Input type="number" className="w-32" value={cfg.montos[e.id]} placeholder="0.00"
                       data-testid={`monto-${e.matricula}`}
                       onChange={(ev) => setCfg({
                         ...cfg, montos: { ...cfg.montos, [e.id]: ev.target.value } })} />
                <Button size="sm" variant="ghost" data-testid={`quitar-monto-${e.matricula}`}
                        onClick={() => setCfg({ ...cfg, montos: { ...cfg.montos, [e.id]: "" } })}>
                  Quitar
                </Button>
              </div>
            ))}
            <p className="text-sm font-semibold">
              <Users className="mr-2 inline h-4 w-4 text-primary" /> Total mensual de la familia:{" "}
              {money(total)}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Se emite el día</Label>
              <Input type="number" min="1" max="28" value={cfg.dia_emision}
                     data-testid={`dia-emision-${fam.tutor_id}`}
                     onChange={(e) => setCfg({ ...cfg, dia_emision: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Vence el día</Label>
              <Input type="number" min="1" max="28" value={cfg.dia_vencimiento}
                     disabled={cfg.fin_de_mes} data-testid={`dia-vence-${fam.tutor_id}`}
                     onChange={(e) => setCfg({ ...cfg, dia_vencimiento: e.target.value })} />
            </div>
            <label className="flex items-center gap-3 pt-7 text-sm">
              <Checkbox checked={cfg.fin_de_mes} data-testid={`fin-de-mes-${fam.tutor_id}`}
                        onCheckedChange={(v) => setCfg({ ...cfg, fin_de_mes: !!v })} />
              <span>Vence el último día del mes</span>
            </label>
          </div>

          {descuentos.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Descuentos y recargos que se aplican cada mes
              </Label>
              {descuentos.map((d) => (
                <label key={d.id} className="flex items-center gap-3 text-sm">
                  <Checkbox checked={cfg.descuento_ids.includes(d.id)}
                            data-testid={`regla-${fam.tutor_id}-${d.id}`}
                            onCheckedChange={(v) => setCfg({
                              ...cfg,
                              descuento_ids: v ? [...cfg.descuento_ids, d.id]
                                               : cfg.descuento_ids.filter((x) => x !== d.id),
                            })} />
                  {d.nombre}
                  <span className={`font-mono text-xs font-semibold ${
                    d.recargo ? "text-destructive" : "text-primary"}`}>
                    {d.recargo ? "+" : "−"}{d.tipo === "porcentaje" ? `${d.valor}%` : money(d.valor)}
                  </span>
                </label>
              ))}
            </div>
          )}

          {fam.ultima_factura && (
            <p className="text-xs text-muted-foreground" data-testid={`ultima-${fam.tutor_id}`}>
              Última factura automática: {fam.ultima_factura.ncf} ·{" "}
              {money(fam.ultima_factura.monto_total)} · {fam.ultima_factura.estado} ·
              periodo {fam.ultima_factura.mensualidad_periodo}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={guardar} disabled={guardando}
                    data-testid={`guardar-${fam.tutor_id}`}>
              <Save className="mr-2 h-4 w-4" /> {guardando ? "Guardando…" : "Guardar mensualidad"}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={generarAhora}
                    disabled={generando || total <= 0} data-testid={`generar-${fam.tutor_id}`}>
              <Zap className="mr-2 h-4 w-4" /> {generando ? "Generando…" : "Generar factura ahora"}
            </Button>
            {fam.activo && (
              <Button variant="ghost" onClick={quitar} data-testid={`eliminar-${fam.tutor_id}`}>
                <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Quitar automático
              </Button>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default function AdminMensualidades() {
  const [datos, setDatos] = useState(null);
  const [descuentos, setDescuentos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [soloActivas, setSoloActivas] = useState(false);
  const [generando, setGenerando] = useState(false);

  const cargar = () => api.get("/mensualidades").then((r) => setDatos(r.data));

  useEffect(() => {
    cargar();
    api.get("/descuentos").then((r) => setDescuentos(r.data)).catch(() => setDescuentos([]));
  }, []);

  const visibles = useMemo(() => {
    const q = norm(busqueda.trim());
    return (datos?.familias || []).filter((f) =>
      (!soloActivas || f.activo)
      && (!q || norm(f.tutor_nombre).includes(q)
          || f.estudiantes.some((e) => norm(e.nombre).includes(q)
                                       || norm(e.matricula || "").includes(q))));
  }, [datos, busqueda, soloActivas]);

  const generarTodas = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post("/mensualidades/generar", { forzar: true });
      toast.success(`${data.generadas} factura(s) emitida(s) por ${money(data.monto)}` +
        (data.ya_estaban ? ` · ${data.ya_estaban} ya estaban` : ""));
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerando(false);
    }
  };

  if (!datos) return <Loading />;

  return (
    <div className="space-y-8" data-testid="admin-mensualidades">
      <PageTitle title="Mensualidad automática"
                 subtitle="Guarde el monto de cada familia y el sistema emite sola su factura consolidada el día que indique. Luego usted la edita, le agrega conceptos o la hace fiscal.">
        <Button className="rounded-full" onClick={generarTodas} disabled={generando}
                data-testid="generar-todas">
          <Zap className="mr-2 h-4 w-4" />
          {generando ? "Generando…" : "Generar las de este mes"}
        </Button>
      </PageTitle>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat icon={Users} label="Familias con mensualidad" value={datos.activas}
              testId="stat-familias" />
        <Stat icon={Wallet} label="Total mensual configurado" value={money(datos.total_mensual)}
              testId="stat-total" />
        <Stat icon={CalendarClock} label={`Facturadas en ${datos.periodo}`}
              value={datos.generadas_este_mes} testId="stat-generadas" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-10" placeholder="Buscar familia, estudiante o matrícula"
                 value={busqueda} data-testid="buscar-familia"
                 onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        <button onClick={() => setSoloActivas(!soloActivas)} data-testid="filtro-activas"
                className={`rounded-full border px-4 py-2 text-sm lift ${
                  soloActivas ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
          Solo con mensualidad activa
        </button>
      </div>

      <div className="space-y-4" data-testid="lista-familias">
        {visibles.map((f) => (
          <FilaFamilia key={f.tutor_id} fam={f} descuentos={descuentos} onGuardado={cargar} />
        ))}
        {visibles.length === 0 && (
          <Empty testId="familias-vacias">Ninguna familia coincide con la búsqueda.</Empty>
        )}
      </div>

      <p className="text-xs text-muted-foreground" data-testid="nota-programacion">
        La generación corre todas las mañanas junto con los recordatorios de pago
        ({fecha(new Date().toISOString())} · 8:00 AM, hora de República Dominicana) y solo
        factura a las familias cuyo día de emisión sea el de hoy. Nunca duplica el mes.
      </p>
    </div>
  );
}
